import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Alert, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import * as XLSX from 'xlsx';
import { logger } from '../utils/logger';

interface XlsxViewerProps {
  /** URL to fetch the xlsx/xls file from */
  source: string;
  /** Optional callback when document is loaded */
  onLoad?: () => void;
  /** Optional callback when error occurs */
  onError?: (error: string) => void;
  /** Whether to use full height */
  fullHeight?: boolean;
}

interface SheetData {
  name: string;
  data: string[][];
}

/**
 * Component to view .xlsx and .xls files using SheetJS (xlsx library)
 * Renders spreadsheet data as HTML tables
 */
export const XlsxViewer: React.FC<XlsxViewerProps> = ({
  source,
  onLoad,
  onError,
  fullHeight = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);

  useEffect(() => {
    const loadXlsx = async () => {
      setLoading(true);
      setError(null);
      setSheets([]);

      try {
        logger.log('📊 XlsxViewer: Fetching xlsx from URL:', source);
        
        const response = await fetch(source, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        logger.log('📊 XlsxViewer: Fetched xlsx, size:', arrayBuffer.byteLength, 'bytes');

        // Parse workbook using xlsx library
        logger.log('📊 XlsxViewer: Parsing workbook...');
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Convert each sheet to array of arrays
        const sheetsData: SheetData[] = workbook.SheetNames.map(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1, defval: '' });
          return {
            name: sheetName,
            data: data as string[][]
          };
        });

        logger.log('📊 XlsxViewer: Parsed', sheetsData.length, 'sheets');
        setSheets(sheetsData);
        onLoad?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Không thể tải bảng tính';
        logger.error('📊 XlsxViewer: Error loading xlsx:', err);
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (source) {
      loadXlsx();
    }
  }, [source, onLoad, onError]);

  const handleSheetChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveSheet(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100%' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography>Đang tải bảng tính Excel...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100%' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const currentSheet = sheets[activeSheet];

  return (
    <Box
      sx={{
        width: '100%',
        height: fullHeight ? '100%' : 'auto',
        minHeight: fullHeight ? '100%' : '400px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff'
      }}
    >
      {/* Sheet tabs */}
      {sheets.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}>
          <Tabs 
            value={activeSheet} 
            onChange={handleSheetChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sheets.map((sheet, index) => (
              <Tab key={index} label={sheet.name} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Sheet content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {currentSheet && currentSheet.data.length > 0 ? (
          <TableContainer component={Paper} sx={{ maxHeight: fullHeight ? 'calc(100vh - 200px)' : '600px' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {currentSheet.data[0]?.map((cell, cellIndex) => (
                    <TableCell 
                      key={cellIndex}
                      sx={{ 
                        fontWeight: 600, 
                        backgroundColor: '#e3f2fd',
                        whiteSpace: 'nowrap',
                        minWidth: 80
                      }}
                    >
                      {cell || `Col ${cellIndex + 1}`}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentSheet.data.slice(1).map((row, rowIndex) => (
                  <TableRow key={rowIndex} hover>
                    {row.map((cell, cellIndex) => (
                      <TableCell 
                        key={cellIndex}
                        sx={{ 
                          whiteSpace: 'nowrap',
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">Sheet trống hoặc không có dữ liệu</Alert>
        )}
      </Box>

      {/* Sheet info */}
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="text.secondary">
          Sheet: {currentSheet?.name} | {currentSheet?.data.length || 0} dòng
        </Typography>
      </Box>
    </Box>
  );
};

export default XlsxViewer;

